/*
 * Copyright (c) 2024-2025. Haodong JU
 */

import { VSelect, VTextarea, VTextField } from 'vuetify/lib/components'
import EnhancedColorPicker from '@/model/baseForm/fields/EnhancedColorPicker.vue'
import EnhancedImageUploader from '@/model/baseForm/fields/EnhancedImageUploader.vue'
import GroupField from '@/model/baseForm/fields/GroupField.vue'
import FunctionItemsSelect from '@/model/baseForm/fields/FunctionItemsSelect.vue'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'

/**
 * Converts IKDataEntity fields to BaseForm schema format
 *
 * @param {Array} formFields - Array of IKDataEntity form fields
 * @param {Object} Types - IKDataEntity.Types enum
 * @returns {Array} - Array of BaseForm schema objects
 */
export function convertToBaseFormSchema (formFields) {
  const Types = IKDataEntity.Types
  // First map all fields to schema objects
  const schemas = formFields.map(field => {
    // Base schema properties
    const schema = {
      name: field.text,
      key: field.value,
      required: field.required,
      hint: field.hint || '',
      default: field.default,
      fullWidth: false,
      tag: field.required ? 'required' : 'optional',
      // Store the original dataType for sorting
      _dataType: field.dataType
    }

    // Set component and componentProps based on field type
    switch (field.dataType) {
      case Types.Group:
        // For group fields, use the GroupField component
        schema.component = GroupField
        schema.componentProps = {
          groupName: field.groupName,
          childKeys: field.childKey,
          labelKey: field.labelKey || 'lang',
          childLabelKey: field.childLabelKey || 'lang'
        }
        break
      case Types.String:
        schema.component = field.type.name === 'text' && field.textArea ? VTextarea : VTextField
        schema.componentProps = {
          outlined: true,
          dense: true,
          rules: field.rule || []
        }
        break
      case Types.Integer:
        schema.component = VTextField
        schema.componentProps = {
          outlined: true,
          dense: true,
          type: 'number',
          step: 1,
          rules: field.rule || []
        }
        break
      case Types.Float:
        schema.component = VTextField
        schema.componentProps = {
          outlined: true,
          dense: true,
          type: 'number',
          step: 0.01,
          rules: field.rule || []
        }
        break
      case Types.Boolean:
        schema.component = VSelect
        schema.required = false
        schema.componentProps = {
          outlined: true,
          dense: true,
          items: [
            { text: 'Yes', value: true },
            { text: 'No', value: false }
          ]
        }
        break
      case Types.Option:
        // Use FunctionItemsSelect if selectItems is a function, otherwise use VSelect
        if (typeof field.type.selectItems === 'function') {
          schema.component = FunctionItemsSelect
          schema.componentProps = {
            outlined: true,
            dense: true,
            selectItemsFunction: field.type.selectItems,
            itemText: field.type.itemText || 'text',
            itemValue: field.type.itemValue || 'value',
            multiple: field.type.multiple || false,
            rules: field.rule || []
          }
        } else {
          schema.component = VSelect
          schema.componentProps = {
            outlined: true,
            dense: true,
            items: field.type.selectItems || [],
            itemText: field.type.itemText || 'text',
            itemValue: field.type.itemValue || 'value',
            multiple: field.type.multiple || false,
            rules: field.rule || []
          }
        }
        break
      case Types.Color:
        schema.component = EnhancedColorPicker
        schema.componentProps = {}
        break
      case Types.Image:
        schema.component = EnhancedImageUploader
        schema.componentProps = {
          maxSizeMB: 15,
          // Add root path from field type if available
          root: field.type && field.type.root ? field.type.root() : '/'
        }
        schema.fullWidth = false
        break
      case Types.Date:
        schema.component = VTextField
        schema.componentProps = {
          outlined: true,
          dense: true,
          type: 'date',
          rules: field.rule || []
        }
        break
      default:
        schema.component = VTextField
        schema.componentProps = {
          outlined: true,
          dense: true,
          rules: field.rule || []
        }
    }

    return schema
  })

  // Sort the schemas according to the specified rules:
  // 1. Group fields should always be first
  // 2. Required fields should come next
  // 3. Non-required fields should be at the end
  return schemas.sort((a, b) => {
    const Types = IKDataEntity.Types

    // If one is a group and the other is not, the group comes first
    if (a._dataType === Types.Group && b._dataType !== Types.Group) {
      return -1
    }
    if (a._dataType !== Types.Group && b._dataType === Types.Group) {
      return 1
    }

    // If both are groups or both are not groups, sort by required status
    if (a.required && !b.required) {
      return -1
    }
    if (!a.required && b.required) {
      return 1
    }

    // If they have the same required status, maintain original order
    return 0
  }).map(schema => {
    // Remove the temporary _dataType property before returning
    const { _dataType, ...cleanSchema } = schema
    return cleanSchema
  })
}
